import { Seo } from '@frontend/shared/web';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFilterStore } from '../../components/filters/filterStore';
import { GlobalFilters } from '../../components/filters/GlobalFilters';
import { CreateIncidentModal } from '../../components/incidents/CreateIncidentModal';
import { IncidentDetailDrawer } from '../../components/incidents/IncidentDetailDrawer';
import { IncidentsFilters } from '../../components/incidents/IncidentsFilters';
import { IncidentsPagination } from '../../components/incidents/IncidentsPagination';
import { IncidentsTable } from '../../components/incidents/IncidentsTable';
import { Header } from '../../components/layout/Header';
import { useAccount } from '../../contexts/AccountContext';
import { useExtranetIncidentsList } from '../../services/api/hooks/useExtranetIncidentsList';
import type { IncidentCategory, IncidentPriority, IncidentStatus } from '../../types/extranet-api.types';

const DEFAULT_LIMIT = 20;
const DEFAULT_SORT_BY: 'createdAt' | 'priority' | 'status' = 'createdAt';
const DEFAULT_SORT_DIR: 'asc' | 'desc' = 'desc';

export const IncidentsRoute = () => {
  const { can } = useAccount();
  const { entityIds, contractIds, siteIds, dateRange } = useFilterStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const canCreate = can('incidents', 'create');

  const page = Math.max(1, Number(searchParams.get('page') ?? 1));
  const limit = Number(searchParams.get('limit') ?? DEFAULT_LIMIT);
  const search = searchParams.get('search') ?? '';
  const status = (searchParams.getAll('status') as IncidentStatus[]) ?? [];
  const category = (searchParams.getAll('category') as IncidentCategory[]) ?? [];
  const priority = (searchParams.getAll('priority') as IncidentPriority[]) ?? [];
  const sortBy = (searchParams.get('sortBy') ?? DEFAULT_SORT_BY) as 'createdAt' | 'priority' | 'status';
  const sortDir = (searchParams.get('sortDir') ?? DEFAULT_SORT_DIR) as 'asc' | 'desc';

  // Modale création : ouverte si createIncident=1 ou createForRide=<id>
  const createForRide = searchParams.get('createForRide') ?? undefined;
  const isCreateOpen = canCreate && (searchParams.get('createIncident') === '1' || Boolean(createForRide));

  const query = useExtranetIncidentsList({
    entityIds,
    contractIds,
    siteIds,
    dateRange,
    page,
    limit,
    search: search || undefined,
    status: status.length > 0 ? status : undefined,
    category: category.length > 0 ? category : undefined,
    priority: priority.length > 0 ? priority : undefined,
    sortBy,
    sortDir,
  });

  // Le back ne livre pas toujours `totalItems` — fallback sur `items.length`.
  const total = query.data?.totalItems ?? query.data?.items?.length ?? 0;

  const updateParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      });
    },
    [setSearchParams],
  );

  const setMultiParam = (key: string, values: string[]) =>
    updateParams(p => {
      p.delete(key);
      values.forEach(v => p.append(key, v));
      p.set('page', '1');
    });

  const setSingleParam = (key: string, value: string) =>
    updateParams(p => {
      if (value) p.set(key, value);
      else p.delete(key);
      if (key !== 'page' && key !== 'sortBy' && key !== 'sortDir' && key !== 'limit') {
        p.set('page', '1');
      }
    });

  const openCreate = () =>
    updateParams(p => {
      p.set('createIncident', '1');
      p.delete('createForRide');
    });

  const closeCreate = () =>
    updateParams(p => {
      p.delete('createIncident');
      p.delete('createForRide');
    });

  return (
    <>
      <Seo title="Incidents — Portail extranet" />
      <Header title="Incidents" subtitle="Suivi et traitement des incidents de course" />

      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 flex flex-col gap-5">
          <GlobalFilters />

          <div className="flex items-center justify-between gap-3">
            <IncidentsFilters
              search={search}
              onSearchChange={v => setSingleParam('search', v)}
              status={status}
              onStatusChange={next => setMultiParam('status', next)}
              category={category}
              onCategoryChange={next => setMultiParam('category', next)}
              priority={priority}
              onPriorityChange={next => setMultiParam('priority', next)}
              totalCount={total}
            />
            {canCreate ? (
              <button
                type="button"
                onClick={openCreate}
                className="h-8 px-3 text-xs font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 transition-colors duration-100 whitespace-nowrap"
              >
                + Nouvelle réclamation
              </button>
            ) : null}
          </div>

          <IncidentsTable
            items={query.data?.items ?? []}
            total={total}
            isLoading={query.isLoading}
            isFetching={query.isFetching}
            isError={query.isError}
            error={query.error}
            selectedId={selectedId}
            onRowClick={item => setSelectedId(item.id)}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={(key, dir) =>
              updateParams(p => {
                p.set('sortBy', key);
                p.set('sortDir', dir);
              })
            }
          />

          <IncidentsPagination
            page={page}
            limit={limit}
            total={total}
            onPageChange={p => setSingleParam('page', String(p))}
            onLimitChange={l =>
              updateParams(p => {
                p.set('limit', String(l));
                p.set('page', '1');
              })
            }
          />
        </div>
      </div>

      <IncidentDetailDrawer incidentId={selectedId} onClose={() => setSelectedId(null)} />

      {isCreateOpen ? (
        <CreateIncidentModal
          initialRideId={createForRide}
          onClose={closeCreate}
          onSuccess={incidentId => {
            // Re-trigger fetch + ouvrir le drawer sur le nouvel incident
            setSelectedId(incidentId);
          }}
        />
      ) : null}
    </>
  );
};
